import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { settingsApi, ApiError, type GeocodeSearchResult } from "@/lib/api";
import { Search } from "lucide-react";

/**
 * Map "find a place" search box — input + explicit search button + results
 * dropdown + admin-enabled status line. Shared by the live-tracking map and
 * the replay map on VehicleTracking.tsx, each wired to its own map instance
 * via `onSelect`.
 */
export function LocationSearchBox({
  token,
  enabled,
  onSelect,
  className,
}: {
  token?: string | null;
  enabled: boolean;
  onSelect: (result: GeocodeSearchResult) => void;
  className?: string;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<GeocodeSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [localEnabled, setLocalEnabled] = useState(enabled);

  useEffect(() => setLocalEnabled(enabled), [enabled]);

  const runSearch = async (q: string) => {
    if (!localEnabled || q.trim().length < 3) return;
    setSearching(true);
    try {
      const r = await settingsApi.searchLocation(q.trim(), token);
      setResults(r);
      setShowResults(true);
    } catch (err) {
      setResults([]);
      setShowResults(true);
      if (err instanceof ApiError && err.status === 403) {
        setLocalEnabled(false);
      } else {
        toast.error(err instanceof ApiError ? err.message : "Location search failed — try again");
      }
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!localEnabled || query.trim().length < 3) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(() => runSearch(query), 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, localEnabled, token]);

  const select = (r: GeocodeSearchResult) => {
    onSelect(r);
    setQuery(r.label);
    setShowResults(false);
  };

  return (
    <div className={className}>
      <div className="flex max-w-md items-start gap-2">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch(query)}
            onFocus={() => results.length > 0 && setShowResults(true)}
            onBlur={() => setTimeout(() => setShowResults(false), 150)}
            placeholder={localEnabled ? "Search for a place on the map…" : "Location search disabled"}
            disabled={!localEnabled}
            className="h-9 pl-8"
          />
          {showResults && localEnabled ? (
            <div className="absolute z-[500] mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-md">
              {searching ? (
                <div className="px-3 py-2 text-xs text-muted-foreground">Searching…</div>
              ) : results.length === 0 ? (
                <div className="px-3 py-2 text-xs text-muted-foreground">No results found.</div>
              ) : (
                results.map((r, i) => (
                  <button
                    key={`${r.lat}-${r.lng}-${i}`}
                    type="button"
                    className="block w-full truncate px-3 py-2 text-left text-xs hover:bg-muted"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => select(r)}
                  >
                    {r.label}
                  </button>
                ))
              )}
            </div>
          ) : null}
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-9"
          disabled={!localEnabled || query.trim().length < 3}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => runSearch(query)}
        >
          <Search className="h-4 w-4" />
        </Button>
      </div>
      <p className="mt-1.5 max-w-md text-[11px] text-muted-foreground">
        {localEnabled
          ? "Location search is enabled."
          : "Location search is disabled by admin. Contact support for assistance."}
      </p>
    </div>
  );
}
